import { createPortal } from 'react-dom';
import type {
  AdminAnalyticsReport,
  AttributionSummary,
  ExecutiveInsight,
  JourneySummary,
  NeedsAttention,
  OperationalDiagnosis,
  PriorityAction,
} from '../types';
import { PRINT_CSS, formatDate, formatTime } from './EodExecutiveReportPrint';

type MorningChartData = {
  journey_summary?: JourneySummary;
  attribution_summary?: AttributionSummary;
  executive_insights?: ExecutiveInsight[];
  needs_attention?: NeedsAttention;
  priority_actions?: PriorityAction[];
  operational_diagnosis?: OperationalDiagnosis[];
  whatsapp_brief?: string;
  cohort_date?: string;
  report_date?: string;
  status_reason_breakdown?: Array<{ status: string; total: number; reasons: Array<{ reason: string; count: number; percentage: number }> }>;
  pipeline?: Array<{ status: string; count: number }>;
  pipeline_total?: number;
};

function num(value: unknown): number {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function pct(count: number, total: number): string {
  if (total <= 0) return '0%';
  return `${((count / total) * 100).toFixed(1)}%`;
}

const VERDICT_LABELS: Record<string, string> = {
  healthy: 'Healthy',
  operational_issue: 'Operational Issue',
  marketing_issue: 'Marketing Issue',
  closing_gap: 'Closing Gap',
  insufficient_data: 'Insufficient Data',
};

export default function MorningManagementReportPrint({ report }: { report: AdminAnalyticsReport }) {
  const charts = (report.chart_data ?? {}) as MorningChartData;
  const summary = (report.overall_summary ?? {}) as Record<string, number>;

  const journey = charts.journey_summary;
  const attribution = charts.attribution_summary;
  const insights = charts.executive_insights ?? [];
  const needs = charts.needs_attention;
  const priorityActions = charts.priority_actions ?? [];
  const operationalDiagnosis = charts.operational_diagnosis ?? [];
  const whatsappBrief = charts.whatsapp_brief ?? '';
  const cohortDate = charts.cohort_date ?? '';
  const reportDate = charts.report_date ?? '';
  const statusReasonBreakdown = charts.status_reason_breakdown ?? [];

  const totalLeads = journey?.total_leads ?? num(summary.total_new_leads);
  const dateStr = reportDate || formatDate(report.window_start);

  // Journey funnel stages
  const funnelStages = journey ? [
    { label: 'Total', value: journey.total_leads },
    { label: 'Assigned', value: journey.assigned },
    { label: 'Called', value: journey.called },
    { label: 'Connected', value: journey.connected },
    { label: 'Marked Qualified', value: journey.qualified },
    ...(journey.qualified_positive !== undefined ? [{ label: 'Positive', value: journey.qualified_positive }] : []),
    { label: 'Won (status)', value: journey.won },
    ...(journey.registered !== undefined ? [{ label: 'Registered', value: journey.registered }] : []),
  ] : [];
  const maxFunnel = Math.max(...funnelStages.map((s) => s.value), 1);

  // KPI cards
  const kpis = [
    { label: "Yesterday's Leads", value: totalLeads, sub: journey ? `${journey.assigned} assigned` : '', accent: '#4B49AC', tint: '#F3F6FF' },
    { label: 'Called', value: journey?.called ?? 0, sub: journey ? `${journey.connected} connected` : '', accent: '#2563eb', tint: '#eff6ff' },
    { label: 'Won (status)', value: journey?.won ?? 0, sub: totalLeads > 0 ? pct(journey?.won ?? 0, totalLeads) : '', accent: '#10b981', tint: '#ecfdf5' },
    { label: 'Lost', value: journey?.lost ?? 0, sub: totalLeads > 0 ? pct(journey?.lost ?? 0, totalLeads) : '', accent: '#e11d48', tint: '#fef2f2' },
    { label: 'Still Open', value: journey?.still_open ?? 0, sub: totalLeads > 0 ? pct(journey?.still_open ?? 0, totalLeads) : '', accent: '#64748b', tint: '#f1f5f9' },
    { label: 'Not Called', value: journey?.not_called ?? 0, sub: totalLeads > 0 ? pct(journey?.not_called ?? 0, totalLeads) : '', accent: '#f97316', tint: '#fff7ed' },
  ];

  return createPortal(
    <>
      <style>{PRINT_CSS}</style>
      <div className="eod-print-report">
        {/* ===== PAGE 1: Header + KPIs + Journey ===== */}
        <div className="eod-print-page">
          {/* Header banner */}
          <div className="print-header-banner">
            <div className="print-header-brand">
              <div className="print-brand-mark">SC</div>
              <div>
                <div className="print-brand-name">Santaan CRM</div>
                <div className="print-brand-sub">Morning Management Report</div>
              </div>
            </div>
            <div className="print-header-meta">
              <h1>Morning Management Report</h1>
              <div className="print-date">{dateStr}</div>
              <div className="print-time">
                Analyzing leads generated: {cohortDate || 'yesterday'}
              </div>
            </div>
          </div>
          {report.generated_at && (
            <div className="print-generated-bar">
              Generated: {formatDate(report.generated_at)} {formatTime(report.generated_at)} IST
            </div>
          )}

          {/* KPI cards */}
          <div className="print-section-label">
            <span className="print-section-label-bar" />
            Yesterday Overview
          </div>
          <div className="print-kpi-grid">
            {kpis.map((k) => (
              <div key={k.label} className="print-kpi-card" style={{ borderLeftColor: k.accent, background: k.tint }}>
                <div className="print-kpi-label">{k.label}</div>
                <div className="print-kpi-value" style={{ color: k.accent }}>{k.value}</div>
                <div className="print-kpi-sub">{k.sub}</div>
              </div>
            ))}
          </div>

          {/* Journey funnel */}
          {journey && journey.total_leads > 0 && (
            <>
              <div className="print-section-label print-section-spacing">
                <span className="print-section-label-bar" />
                Yesterday's Lead Journey
              </div>
              <div className="print-flow-visual">
                {funnelStages.map((s) => (
                  <div key={s.label} className="print-flow-row">
                    <div className="print-flow-label">{s.label}</div>
                    <div className="print-flow-bar-container">
                      <div
                        className="print-flow-bar"
                        style={{
                          width: `${Math.max((s.value / maxFunnel) * 100, 2)}%`,
                          background: 'linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%)',
                        }}
                      >
                        <span className="print-flow-count">{s.value}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {/* Fallout summary */}
              <div className="print-kpi-grid" style={{ marginTop: '14px' }}>
                <div className="print-kpi-card" style={{ borderLeftColor: '#f97316', background: '#fff7ed' }}>
                  <div className="print-kpi-label">Unassigned</div>
                  <div className="print-kpi-value" style={{ color: '#f97316' }}>{journey.unassigned}</div>
                </div>
                <div className="print-kpi-card" style={{ borderLeftColor: '#64748b', background: '#f1f5f9' }}>
                  <div className="print-kpi-label">Not Called</div>
                  <div className="print-kpi-value" style={{ color: '#64748b' }}>{journey.not_called}</div>
                </div>
                <div className="print-kpi-card" style={{ borderLeftColor: '#2563eb', background: '#eff6ff' }}>
                  <div className="print-kpi-label">{journey.no_patient_outcome !== undefined ? 'Patient No Answer' : 'Called, Not Connected'}</div>
                  <div className="print-kpi-value" style={{ color: '#2563eb' }}>{journey.not_connected}</div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* ===== PAGE 2: Needs Attention + Attribution ===== */}
        <div className="eod-print-page">
          {/* Needs Attention */}
          {needs && (
            <>
              <div className="print-section-label">
                <span className="print-section-label-bar" />
                Needs Attention Today
              </div>
              <div className="print-kpi-grid">
                {[
                  { label: 'Unassigned', value: needs.yesterday_unassigned },
                  { label: 'Not Called', value: needs.yesterday_not_called },
                  { label: 'Called, Never Connected', value: needs.yesterday_not_connected },
                  { label: 'Follow-ups Due', value: needs.followups_due_today },
                  { label: 'Overdue Follow-ups', value: needs.followups_overdue },
                  { label: 'AI Hot (Tier 1) Pending', value: needs.hot_pending },
                  { label: 'AI Warm (Tier 2) Pending', value: needs.warm_pending },
                ].filter((item) => item.value > 0).map((item) => (
                  <div key={item.label} className="print-kpi-card" style={{ borderLeftColor: '#f59e0b', background: '#fffbeb' }}>
                    <div className="print-kpi-label">{item.label}</div>
                    <div className="print-kpi-value" style={{ color: '#b45309' }}>{item.value}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Attribution summary — campaigns */}
          {attribution && attribution.campaigns.length > 0 && (
            <>
              <div className="print-section-label print-section-spacing">
                <span className="print-section-label-bar" />
                Campaign Attribution
              </div>
              <table className="print-agent-table" style={{ width: '100%', fontSize: '10px', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>
                    <th style={{ padding: '6px 4px' }}>Campaign</th>
                    <th style={{ padding: '6px 4px', textAlign: 'right' }}>Leads</th>
                    <th style={{ padding: '6px 4px', textAlign: 'right' }}>Asgn</th>
                    <th style={{ padding: '6px 4px', textAlign: 'right' }}>Called</th>
                    <th style={{ padding: '6px 4px', textAlign: 'right' }}>Conn</th>
                    <th style={{ padding: '6px 4px', textAlign: 'right' }}>Won (status)</th>
                    <th style={{ padding: '6px 4px', textAlign: 'right' }}>Lost</th>
                    <th style={{ padding: '6px 4px', textAlign: 'right' }}>Won-status %</th>
                  </tr>
                </thead>
                <tbody>
                  {attribution.campaigns.slice(0, 10).map((c, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '5px 4px', fontWeight: 600 }}>{c.campaign_name ?? c.campaign_id ?? 'Unknown'}</td>
                      <td style={{ padding: '5px 4px', textAlign: 'right' }}>{c.total_leads}</td>
                      <td style={{ padding: '5px 4px', textAlign: 'right' }}>{c.assigned}</td>
                      <td style={{ padding: '5px 4px', textAlign: 'right' }}>{c.called}</td>
                      <td style={{ padding: '5px 4px', textAlign: 'right' }}>{c.connected}</td>
                      <td style={{ padding: '5px 4px', textAlign: 'right', color: '#10b981', fontWeight: 600 }}>{c.won}</td>
                      <td style={{ padding: '5px 4px', textAlign: 'right', color: '#e11d48' }}>{c.lost}</td>
                      <td style={{ padding: '5px 4px', textAlign: 'right' }}>{c.conversion_rate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {/* Operational diagnosis */}
          {operationalDiagnosis.length > 0 && (
            <>
              <div className="print-section-label print-section-spacing">
                <span className="print-section-label-bar" />
                Operational vs Marketing Diagnosis
              </div>
              <table className="print-agent-table" style={{ width: '100%', fontSize: '10px', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>
                    <th style={{ padding: '6px 4px' }}>Campaign</th>
                    <th style={{ padding: '6px 4px', textAlign: 'right' }}>Leads</th>
                    <th style={{ padding: '6px 4px', textAlign: 'right' }}>Asgn</th>
                    <th style={{ padding: '6px 4px', textAlign: 'right' }}>Called</th>
                    <th style={{ padding: '6px 4px', textAlign: 'right' }}>Conn</th>
                    <th style={{ padding: '6px 4px', textAlign: 'right' }}>Won (status)</th>
                    <th style={{ padding: '6px 4px' }}>Verdict</th>
                  </tr>
                </thead>
                <tbody>
                  {operationalDiagnosis.slice(0, 10).map((row, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '5px 4px', fontWeight: 600 }}>{row.campaign}</td>
                      <td style={{ padding: '5px 4px', textAlign: 'right' }}>{row.total_leads}</td>
                      <td style={{ padding: '5px 4px', textAlign: 'right' }}>{row.assigned}</td>
                      <td style={{ padding: '5px 4px', textAlign: 'right' }}>{row.called}</td>
                      <td style={{ padding: '5px 4px', textAlign: 'right' }}>{row.connected}</td>
                      <td style={{ padding: '5px 4px', textAlign: 'right' }}>{row.won}</td>
                      <td style={{ padding: '5px 4px' }}>{VERDICT_LABELS[row.verdict] ?? row.verdict}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>

        {/* ===== PAGE 3: Reasons + Insights + WhatsApp ===== */}
        <div className="eod-print-page">
          {/* Reason breakdown */}
          {statusReasonBreakdown.length > 0 && (
            <>
              <div className="print-section-label">
                <span className="print-section-label-bar" />
                Outcome Reasons
              </div>
              {statusReasonBreakdown.map((sr) => (
                <div key={sr.status} className="print-reason-block" style={{ marginBottom: '14px' }}>
                  <div style={{ fontWeight: 700, fontSize: '12px', marginBottom: '6px', textTransform: 'capitalize' }}>
                    {sr.status} ({sr.total})
                  </div>
                  {sr.reasons.map((r) => (
                    <div key={r.reason} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', padding: '2px 0' }}>
                      <span>{r.reason}</span>
                      <span style={{ fontWeight: 600 }}>{r.count} ({r.percentage}%)</span>
                    </div>
                  ))}
                </div>
              ))}
            </>
          )}

          {/* Executive insights */}
          {insights.length > 0 && (
            <>
              <div className="print-section-label print-section-spacing">
                <span className="print-section-label-bar" />
                Priority Insights
              </div>
              {insights.map((ins) => (
                <div key={ins.type} className="print-key-finding" style={{ marginBottom: '12px' }}>
                  <div style={{ fontWeight: 700, fontSize: '12px', marginBottom: '4px' }}>{ins.title}</div>
                  {ins.details.map((d, i) => (
                    <div key={i} style={{ fontSize: '10px', padding: '1px 0' }}>
                      • {d.label}: {d.metric_label}
                    </div>
                  ))}
                </div>
              ))}
            </>
          )}

          {/* Priority actions */}
          {priorityActions.length > 0 && (
            <>
              <div className="print-section-label print-section-spacing">
                <span className="print-section-label-bar" />
                Priority Actions
              </div>
              {priorityActions.map((action, i) => (
                <div key={i} style={{ fontSize: '11px', padding: '3px 0', display: 'flex', gap: '8px' }}>
                  <span style={{ fontWeight: 700, color: action.priority === 'high' ? '#e11d48' : action.priority === 'medium' ? '#b45309' : '#64748b' }}>
                    [{action.priority.toUpperCase()}]
                  </span>
                  <span>{action.text}</span>
                </div>
              ))}
            </>
          )}

          {/* WhatsApp brief */}
          {whatsappBrief && (
            <>
              <div className="print-section-label print-section-spacing">
                <span className="print-section-label-bar" />
                WhatsApp Brief
              </div>
              <pre style={{ whiteSpace: 'pre-wrap', fontSize: '10px', fontFamily: 'inherit', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '10px' }}>
                {whatsappBrief}
              </pre>
            </>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}
